import { useMemo, useState, useRef, useEffect, useCallback } from 'react'
import { Table } from 'antd'
import { CaretLeftOutlined, CaretRightOutlined } from '@ant-design/icons'
import { useSpring, animated } from 'react-spring'
import { FixedSizeList as List } from 'react-window'
import {
  calculateColumnWidthBasedOnText,
  filterDistinctByProperty,
  handleDiscountCellWidth,
  handleMultipleTableVirtualScrollX,
  handleVirtualScroll,
} from '../../Helpers/GenericHelpers'
import DiscountInput from './DiscountInput'
import { toggleSeasonConfirmChangeModal } from '../../Slices/Omnichannel/SeasonRuleSlice'
import {
  resetPriceListsToCopyTo,
  setActivePriceListToBeCopy,
  doPriceListCopy,
  setPriceListsToCopyTo,
  setListingCopyPopover,
  TypeOfChannel,
} from '../../Slices/Omnichannel/DiscountSlice'

import { listingsAllValue } from '../../Constants/Configuration'
import { useAppDispatch, useAppSelector } from '../../hooks'
import { useNavigateWrapper } from '../../Helpers/NavigateHelper'
import { calcRuleTableHeight } from 'Helpers/TableHelper'
import { columnCloseWidth } from 'Constants/TableConfiguration'
import DiscountSuperGroupTableHeader from './DiscountSuperGroupTableHeader'
import { retrieveActiveListings } from 'Helpers/DiscountHelper'

type Props = {
  className: string
  superGroup: any
  filteredBrands: any
  saveRule: any
  sectorDiscounts: Map<string, any>
  priceListDiscounts: Map<string, any>
  brandDiscounts: Map<string, any>
  createSubrule: any
  editDisabled: boolean
  currentRule: any
  currentListing: any
  scrollX: number
}

function DiscountSuperGroupTable(props: Props) {
  const dispatch = useAppDispatch()
  const { navigateToChannels } = useNavigateWrapper()
  const ruleId = useAppSelector(state => state.present.seasonRule.ruleId)
  const hasRuleSelectedChangesToSave = useAppSelector(state => state.present.seasonRule.hasRuleSelectedChangesToSave)
  const priceListsToCopyTo = useAppSelector(state => state.present.discount.priceListsToCopyTo)
  const typeOfChannel: TypeOfChannel = useAppSelector(state => state.present.discount.typeOfChannel)
  const allPricelists = useAppSelector(state => state.present.configuration.allPricelists)
  const [isColumnOpen, setIsColumnOpen] = useState(true)
  const [isColumnMinified, setIsColumnMinified] = useState(false)
  const [componentWidth, setComponentWidth] = useState(0)
  const [isVirtualized, setIsVirtualized] = useState(false)

  const currentActiveListings = useMemo(() => {
    return retrieveActiveListings(typeOfChannel, allPricelists)
  }, [typeOfChannel, allPricelists])

  const tableRef = useRef<any>(undefined)
  const virtualListRef = useRef<HTMLDivElement>(undefined)
  const multipleTableContainerRef: HTMLDivElement = document.getElementById(
    'discount-tables-container',
  ) as HTMLDivElement

  const superGroup = props.superGroup.name ? props.superGroup.name : ''
  const superGroupId = props.superGroup.id ? props.superGroup.id : undefined

  const sectorDiscountsFiltered = useMemo(() => {
    return Array.from(props.sectorDiscounts.values()).filter(x => x.supergroup === superGroupId)
  }, [props.sectorDiscounts, superGroupId])

  const sectorDistinct = useMemo(() => {
    return sectorDiscountsFiltered.filter((value, index, self) => filterDistinctByProperty(value, index, self, 'id'))
  }, [sectorDiscountsFiltered])

  const priceListDiscountsBySupergroup = useMemo(() => {
    return Array.from(props.priceListDiscounts.values())
      .filter(x => x.supergroup === superGroupId)
      .sort((a, b) => {
        const prevIndex = currentActiveListings.findIndex(item => item.name === a.channel)
        const nextIndex = currentActiveListings.findIndex(item => item.name === b.channel)
        return prevIndex - nextIndex
      })
  }, [currentActiveListings, props.priceListDiscounts, superGroupId])

  const brandDiscountsBySuperGroup = useMemo(() => {
    return Array.from(props.brandDiscounts.values()).filter(x => x.supergroup === superGroupId)
  }, [props.brandDiscounts, superGroupId])

  const openSubRuleView = useCallback(
    (e, subRuleInfo) => {
      if (props.editDisabled) {
        return
      }
      e.stopPropagation()
      props.createSubrule(subRuleInfo.superGroupValue, subRuleInfo.sectorValue, subRuleInfo.brandValue)
    },
    [props],
  )

  const sendPriceListDiscountsCopyByChannel = useCallback(() => {
    if (priceListsToCopyTo.length === 0) {
      return
    }

    dispatch(doPriceListCopy())

    dispatch(setActivePriceListToBeCopy(undefined))
    dispatch(resetPriceListsToCopyTo())
    dispatch(setListingCopyPopover({ superGroup: null, sector: null, channel: null }))
  }, [dispatch, priceListsToCopyTo.length])

  const showSettingsView = useCallback(() => {
    if (hasRuleSelectedChangesToSave) {
      dispatch(toggleSeasonConfirmChangeModal(true))
      return
    }
    navigateToChannels()
  }, [dispatch, hasRuleSelectedChangesToSave])

  const checkPriceListToCopyTo = useCallback(
    (superGroupId, sectorId, channelName) => {
      dispatch(
        setPriceListsToCopyTo({
          superGroup: superGroupId,
          sector: sectorId,
          channel: channelName,
        }),
      )
    },
    [dispatch],
  )

  const closeCopyPopover = useCallback(() => {
    dispatch(setActivePriceListToBeCopy(undefined))
    dispatch(resetPriceListsToCopyTo())
    dispatch(setListingCopyPopover({ superGroup: null, sector: null, channel: null }))
  }, [dispatch])

  const sectorChildren = useMemo(() => {
    return sectorDistinct.map((sector, sectorIndex, sectorArray) => {
      const channelFilterdByListingSelect = priceListDiscountsBySupergroup.filter(
        value =>
          value.sector === sector.id &&
          (props.currentListing?.find(y => y.value === listingsAllValue) || props.currentListing.length === 0
            ? value
            : props.currentListing?.find(y => y.value === value.channel)),
      )
      return {
        title: (
          <div className='super-group-sector-head-box' title={superGroup + ' ' + sector.name}>
            <span
              className={props.editDisabled ? '' : 'text-underline cursor-pointer'}
              onClick={e => {
                openSubRuleView(e, { superGroupValue: superGroup, sectorValue: sector.name, brandValue: undefined })
              }}
            >
              {sector.name}
            </span>
            <div className='sector-input-box'>
              <DiscountInput
                superGroup={superGroupId}
                sector={sector.id}
                channel={null}
                brand={null}
                sectorDiscounts={props.sectorDiscounts}
                priceListDiscounts={props.priceListDiscounts}
                brandDiscounts={props.brandDiscounts}
                editDisabled={props.editDisabled || !props.currentListing?.find(y => y.value === listingsAllValue)}
              />
            </div>
          </div>
        ),
        dataIndex: sector.name,
        key: sector.name + '-' + superGroup,
        children: channelFilterdByListingSelect.map((channel, channelIndex, channelArray) => {
          return {
            title: (
              <DiscountSuperGroupTableHeader
                channel={channel}
                channelIndex={channelIndex}
                channelArray={channelArray}
                sectorIndex={sectorIndex}
                sectorArray={sectorArray}
                superGroup={superGroup}
                sector={sector}
                priceListsToCopyTo={priceListsToCopyTo}
                superGroupId={superGroupId}
                editDisabled={props.editDisabled}
                sectorDiscounts={props.sectorDiscounts}
                priceListDiscounts={props.priceListDiscounts}
                brandDiscounts={props.brandDiscounts}
                showSettingsView={showSettingsView}
                sendPriceListDiscountsCopyByChannel={sendPriceListDiscountsCopyByChannel}
                closeCopyPopover={closeCopyPopover}
                checkPriceListToCopyTo={checkPriceListToCopyTo}
                width={handleDiscountCellWidth(channel.channel, channelFilterdByListingSelect.length)}
              />
            ),
            dataIndex: channel.channel + sector.name,
            key: channel.channel + sector.name,
            width: handleDiscountCellWidth(channel.channel, channelFilterdByListingSelect.length),
            render: (text, record, index) => {
              return !record['noArticles' + channel.channel + sector.name] ? (
                <DiscountInput
                  superGroup={superGroupId}
                  sector={sector.id}
                  channel={channel.channel}
                  brand={record.key}
                  sectorDiscounts={props.sectorDiscounts}
                  priceListDiscounts={props.priceListDiscounts}
                  brandDiscounts={props.brandDiscounts}
                  editDisabled={props.editDisabled}
                  arrowNavigation
                />
              ) : null
            },
          }
        }),
      }
    })
  }, [
    sectorDistinct,
    priceListDiscountsBySupergroup,
    props.editDisabled,
    props.sectorDiscounts,
    props.priceListDiscounts,
    props.brandDiscounts,
    props.currentListing,
    superGroupId,
    superGroup,
    openSubRuleView,
    priceListsToCopyTo,
    showSettingsView,
    sendPriceListDiscountsCopyByChannel,
    closeCopyPopover,
    checkPriceListToCopyTo,
  ])

  const superGroupTableColumns = useMemo(() => {
    return [
      {
        title: (
          <div className='super-group-table-head-box' title={superGroup}>
            {isColumnOpen ? (
              <>
                <CaretLeftOutlined
                  className='multi-table-collapse-icon'
                  onClick={() => {
                    setIsColumnOpen(false)
                  }}
                />{' '}
                <span
                  className={props.editDisabled ? '' : 'text-underline cursor-pointer'}
                  onClick={e => {
                    openSubRuleView(e, { superGroupValue: superGroup, sectorValue: undefined, brandValue: undefined })
                  }}
                >
                  {superGroup}
                </span>
              </>
            ) : (
              <>
                {isColumnMinified ? (
                  <span>
                    <CaretRightOutlined
                      className='multi-table-collapse-icon'
                      title={superGroup}
                      onClick={() => {
                        setIsColumnOpen(true)
                      }}
                    />
                  </span>
                ) : (
                  <span>
                    <CaretRightOutlined
                      className='multi-table-collapse-icon'
                      title={superGroup}
                      onClick={() => {
                        setIsColumnOpen(true)
                      }}
                    />
                  </span>
                )}
              </>
            )}
          </div>
        ),
        dataIndex: 'superGroup',
        key: 'superGroup-' + superGroup,
        children: sectorChildren,
      },
    ]
  }, [isColumnOpen, props.editDisabled, superGroup, isColumnMinified, sectorChildren, openSubRuleView])

  const superGroupTableData = useMemo(() => {
    return props.filteredBrands.map(brand => {
      const currentBrand = {
        key: brand.id,
      }

      const currentBrandDiscounts = brandDiscountsBySuperGroup.filter(x => x.id === brand.id)

      if (currentBrandDiscounts.length > 0) {
        for (var i = 0; i < currentBrandDiscounts.length; i++) {
          const sectorId = currentBrandDiscounts[i].sector
          const sector = sectorDistinct.find(x => x.id === sectorId)
          currentBrand['noArticles' + currentBrandDiscounts[i].channel + sector?.name] =
            currentBrandDiscounts[i].noArticles
        }
      }

      return currentBrand
    })
  }, [brandDiscountsBySuperGroup, props.filteredBrands, sectorDistinct])

  useEffect(() => {
    setTimeout(() => {
      setComponentWidth(
        (tableRef?.current?.querySelector('.ant-table-container .ant-table-thead') as HTMLElement)?.offsetWidth + 1,
      )
    }, 100)
  }, [ruleId, props.currentListing])

  useEffect(() => {
    setTimeout(() => {
      if (isColumnOpen) {
        handleMultipleTableVirtualScrollX(props.scrollX, tableRef, componentWidth, setIsVirtualized)
      }
    }, 200)
  }, [props.scrollX, tableRef, isColumnOpen, componentWidth])

  const renderVirtualList = (rawData: object[], { scrollbarSize, ref, onScroll }: any) => {
    const trueColumns = superGroupTableColumns.flatMap(a => a.children).flatMap(x => x.children)

    return (
      <List
        className='virtual-list'
        height={Math.min(calcRuleTableHeight(multipleTableContainerRef?.offsetHeight), rawData.length * 30)}
        itemCount={rawData.length}
        itemSize={30}
        innerElementType={'table'}
        outerElementType={'div'}
        width={componentWidth} /* width is not fixed... */
        overscanCount={3}
        ref={virtualListRef}
        onScroll={({
          scrollDirection,
          scrollOffset,
          scrollUpdateWasRequested,
        }: {
          scrollDirection: string
          scrollOffset: number
          scrollUpdateWasRequested: boolean
        }) => {
          handleVirtualScroll(virtualListRef.current, scrollOffset)
        }}
      >
        {({ index, style }: { index: number; style: React.CSSProperties }) => (
          <tbody className='ant-table-tbody'>
            <tr data-row={index} className='ant-table-row ant-table-row-level-0' style={style}>
              {trueColumns.map(column => {
                return (
                  <td className='ant-table-cell' style={{ width: column.width }} key={column.key + index}>
                    {isColumnOpen && !isVirtualized ? column.render(undefined, rawData[index], index) : null}
                  </td>
                )
              })}
            </tr>
          </tbody>
        )}
      </List>
    )
  }

  return (
    <div className='ml-1' ref={tableRef}>
      <animated.div
        style={useSpring({
          width: isColumnOpen ? componentWidth : columnCloseWidth,
          onRest: () => {
            setIsColumnMinified(!isColumnOpen)
          },
        })}
        className={'supergroup multi-table-slide-container' + (isColumnOpen ? ' open' : ' closed')}
      >
        <Table
          scroll={{ y: calcRuleTableHeight(multipleTableContainerRef?.offsetHeight) }}
          className={'discount-super-group-table multi-table same-table-scroll' + props.className}
          columns={superGroupTableColumns}
          dataSource={superGroupTableData}
          pagination={false}
          components={{
            body: renderVirtualList,
          }}
        />
      </animated.div>
    </div>
  )
}

export default DiscountSuperGroupTable
